import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, db } from './App';
import { functions } from './App';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc } from 'firebase/firestore';

const Settings = () => {
  const navigate = useNavigate();
  const { user, shopId, shopData, logout } = useAuth();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelled, setCancelled] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Editing State
  const [editingAddress, setEditingAddress] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({ street: '', city: '', state: '', zipCode: '' });

  const [editingPhone, setEditingPhone] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [phoneForm, setPhoneForm] = useState('');

  const [editingAdvisor, setEditingAdvisor] = useState(false);
  const [savingAdvisor, setSavingAdvisor] = useState(false);
  const [advisorForm, setAdvisorForm] = useState('');

  const intakeUrl = `${window.location.origin}/s/${shopId}/intake`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(intakeUrl)}`;

  const copyLink = async () => {
    await navigator.clipboard.writeText(intakeUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const startEditingAddress = () => {
    if (shopData && shopData.address) {
      setAddressForm({
        street: shopData.address.street || '',
        city: shopData.address.city || '',
        state: shopData.address.state || '',
        zipCode: shopData.address.zipCode || ''
      });
    }
    setEditingAddress(true);
  };

  const startEditingPhone = () => {
    setPhoneForm(shopData?.ownerPhone || '');
    setEditingPhone(true);
  };

  const handleUpdateAddress = async (e) => {
    e.preventDefault();
    setSavingAddress(true);
    try {
      const fullAddress = `${addressForm.street}, ${addressForm.city}, ${addressForm.state} ${addressForm.zipCode}`;
      await updateDoc(doc(db, 'shops', shopId), {
        address: {
          ...addressForm,
          full: fullAddress
        }
      });
      setEditingAddress(false);
    } catch (err) {
      alert('Failed to update address: ' + err.message);
    } finally {
      setSavingAddress(false);
    }
  };

  const handleUpdatePhone = async (e) => {
    e.preventDefault();
    setSavingPhone(true);
    try {
      await updateDoc(doc(db, 'shops', shopId), {
        ownerPhone: phoneForm
      });
      setEditingPhone(false);
    } catch (err) {
      alert('Failed to update phone: ' + err.message);
    } finally {
      setSavingPhone(false);
    }
  };

  const handleUpdateAdvisor = async (e) => {
    e.preventDefault();
    setSavingAdvisor(true);
    try {
      await updateDoc(doc(db, 'shops', shopId), {
        advisorName: advisorForm.trim() || 'Kenneth'
      });
      setEditingAdvisor(false);
    } catch (err) {
      alert('Failed to update advisor name: ' + err.message);
    } finally {
      setSavingAdvisor(false);
    }
  };

  const formatPhoneInput = (e) => {
    let v = e.target.value.replace(/\D/g, '');
    if (v.length >= 10) v = `(${v.slice(0,3)}) ${v.slice(3,6)}-${v.slice(6,10)}`;
    setPhoneForm(v);
  };

  const downloadQR = () => {
    const link = document.createElement('a');
    link.href = qrUrl;
    link.download = `${(shopData?.shopName || 'shop').replace(/\s+/g, '-').toLowerCase()}-checkin-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const printQR = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Customer Check-In QR Code</title>
          <style>
            body { font-family: Arial, sans-serif; text-align: center; padding: 40px; }
            h1 { font-size: 28px; margin-bottom: 10px; }
            h2 { font-size: 20px; color: #666; margin-bottom: 30px; font-weight: normal; }
            img { margin: 20px 0; }
            .url { font-size: 14px; color: #888; margin-top: 30px; word-break: break-all; }
          </style>
        </head>
        <body>
          <h1>${shopData?.shopName || 'Customer Check-In'}</h1>
          <h2>Scan to Check In Your Vehicle</h2>
          <img src="${qrUrl}" alt="QR Code" width="300" height="300" />
          <p class="url">${intakeUrl}</p>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    setCancelError(null);
    
    try {
      const cancelSubscription = httpsCallable(functions, 'cancelSubscription');
      await cancelSubscription({ shopId });
      setCancelled(true);
      setShowCancelModal(false);
    } catch (err) {
      console.error('Cancel error:', err);
      setCancelError('Failed to cancel subscription. Please contact support.');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">Active</span>;
      case 'past_due':
        return <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">Past Due</span>;
      case 'canceled':
        return <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm font-medium">Canceled</span>;
      case 'pending':
        return <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">Pending</span>;
      default:
        return <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm font-medium">{status || 'Unknown'}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50 shadow-xl">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-slate-800 rounded-lg transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold">Settings</h1>
                <p className="text-slate-400 text-sm">{shopData?.shopName}</p>
              </div>
            </div>
            <button 
              onClick={() => { logout(); navigate('/login'); }} 
              className="px-3 py-1 bg-slate-700 rounded text-sm hover:bg-slate-600 transition"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        
        {/* Subscription Section */}
        <section className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
            <h2 className="text-lg font-bold text-slate-800">Subscription</h2>
          </div>
          <div className="p-6">
            {cancelled ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-slate-800 mb-2">Subscription Canceled</h3>
                <p className="text-slate-500">Your subscription has been canceled. You'll have access until the end of your billing period.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-sm text-slate-500">Plan</p>
                    <p className="text-lg font-semibold text-slate-800">Authorization Hook Monthly</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Price</p>
                    <p className="text-lg font-semibold text-slate-800">$50/month</p>
                  </div>
                </div>

                <div className="flex items-center justify-between py-4 border-t border-slate-100">
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <div className="mt-1">{getStatusBadge(shopData?.subscriptionStatus)}</div>
                  </div>
                  {shopData?.lastPaymentAt && (
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Last Payment</p>
                      <p className="text-sm text-slate-700">
                        {shopData.lastPaymentAt.toDate ? 
                          shopData.lastPaymentAt.toDate().toLocaleDateString() : 
                          new Date(shopData.lastPaymentAt).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>

                {shopData?.subscriptionStatus === 'active' && (
                  <div className="pt-4 border-t border-slate-100">
                    <button
                      onClick={() => setShowCancelModal(true)}
                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      Cancel Subscription
                    </button>
                  </div>
                )}

                {shopData?.subscriptionStatus === 'past_due' && (
                  <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-red-700 text-sm">
                      Your payment failed. Please update your payment method to continue using Authorization Hook.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Customer Check-In Link Section */}
        <section className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
            <h2 className="text-lg font-bold text-slate-800">Customer Check-In Link</h2>
          </div>
          <div className="p-6">
            <p className="text-slate-600 text-sm mb-4">Share this link with your customers so they can check in their vehicles.</p>
            
            {/* URL Display */}
            <div className="bg-slate-100 rounded-lg p-4 mb-4">
              <p className="text-sm font-mono text-slate-700 break-all">{intakeUrl}</p>
              <button 
                onClick={copyLink}
                className="mt-3 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition"
              >
                {copied ? '✓ Copied!' : 'Copy Link'}
              </button>
            </div>

            {/* QR Code */}
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="bg-white border-2 border-slate-200 rounded-xl p-4">
                <img src={qrUrl} alt="QR Code" className="w-40 h-40" />
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={downloadQR}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download QR Code
                </button>
                <button
                  onClick={printQR}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print QR Code
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Shop Info Section */}
        <section className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
            <h2 className="text-lg font-bold text-slate-800">Shop Information</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Shop Name</p>
                <p className="text-slate-800 font-medium">{shopData?.shopName || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Owner</p>
                <p className="text-slate-800 font-medium">{shopData?.ownerName || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="text-slate-800 font-medium">{shopData?.ownerEmail || '-'}</p>
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <p className="text-sm text-slate-500">Phone</p>
                  {!editingPhone && (
                    <button onClick={startEditingPhone} className="text-amber-600 hover:text-amber-700 text-xs font-medium">Edit</button>
                  )}
                </div>
                {editingPhone ? (
                  <form onSubmit={handleUpdatePhone} className="flex space-x-2">
                    <input
                      type="tel"
                      value={phoneForm}
                      onChange={formatPhoneInput}
                      className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-amber-400 outline-none"
                      required
                    />
                    <button type="submit" disabled={savingPhone} className="text-emerald-600 hover:text-emerald-700">✓</button>
                    <button type="button" onClick={() => setEditingPhone(false)} className="text-red-600 hover:text-red-700">✕</button>
                  </form>
                ) : (
                  <p className="text-slate-800 font-medium">{shopData?.ownerPhone || '-'}</p>
                )}
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <p className="text-sm text-slate-500">AI Advisor Name</p>
                  {!editingAdvisor && (
                    <button onClick={() => { setAdvisorForm(shopData?.advisorName || ''); setEditingAdvisor(true); }} className="text-amber-600 hover:text-amber-700 text-xs font-medium">Edit</button>
                  )}
                </div>
                {editingAdvisor ? (
                  <form onSubmit={handleUpdateAdvisor} className="flex space-x-2">
                    <input
                      type="text"
                      value={advisorForm}
                      onChange={e => setAdvisorForm(e.target.value)}
                      placeholder="Kenneth"
                      maxLength={32}
                      className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-amber-400 outline-none"
                    />
                    <button type="submit" disabled={savingAdvisor} className="text-emerald-600 hover:text-emerald-700">✓</button>
                    <button type="button" onClick={() => setEditingAdvisor(false)} className="text-red-600 hover:text-red-700">✕</button>
                  </form>
                ) : (
                  <p className="text-slate-800 font-medium">{shopData?.advisorName || 'Kenneth'}</p>
                )}
              </div>
            </div>
            
            <div className="border-t border-slate-100 pt-4">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm text-slate-500">Address</p>
                {!editingAddress && (
                  <button onClick={startEditingAddress} className="text-amber-600 hover:text-amber-700 text-sm font-medium">
                    Edit
                  </button>
                )}
              </div>
              
              {editingAddress ? (
                <form onSubmit={handleUpdateAddress} className="bg-slate-50 p-4 rounded-lg space-y-3">
                  <div className="grid grid-cols-1 gap-3">
                    <input 
                      type="text" 
                      placeholder="Street Address"
                      value={addressForm.street}
                      onChange={e => setAddressForm({...addressForm, street: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-amber-400 outline-none"
                      required 
                    />
                    <div className="grid grid-cols-3 gap-3">
                      <input 
                        type="text" 
                        placeholder="City"
                        value={addressForm.city}
                        onChange={e => setAddressForm({...addressForm, city: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-amber-400 outline-none"
                        required 
                      />
                      <input 
                        type="text" 
                        placeholder="State"
                        value={addressForm.state}
                        onChange={e => setAddressForm({...addressForm, state: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-amber-400 outline-none"
                        required 
                      />
                      <input 
                        type="text" 
                        placeholder="ZIP"
                        value={addressForm.zipCode}
                        onChange={e => setAddressForm({...addressForm, zipCode: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-amber-400 outline-none"
                        required 
                      />
                    </div>
                  </div>
                  <div className="flex space-x-2 justify-end pt-2">
                    <button 
                      type="button" 
                      onClick={() => setEditingAddress(false)}
                      className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 font-medium"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      disabled={savingAddress}
                      className="px-4 py-2 bg-amber-500 text-white rounded text-sm font-medium hover:bg-amber-600 disabled:opacity-50"
                    >
                      {savingAddress ? 'Saving...' : 'Save Address'}
                    </button>
                  </div>
                </form>
              ) : (
                <p className="text-slate-800 font-medium">{shopData?.address?.full || '-'}</p>
              )}
            </div>
          </div>
        </section>

        {/* Account Section */}
        <section className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
            <h2 className="text-lg font-bold text-slate-800">Account</h2>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <p className="text-sm text-slate-500">Email</p>
              <p className="text-slate-800 font-medium">{user?.email || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Shop ID</p>
              <p className="text-slate-600 font-mono text-sm">{shopId || '-'}</p>
            </div>
          </div>
        </section>

        {/* Data Retention Notice */}
        <div className="text-center text-slate-500 text-sm py-4">
          All uploaded images (license plates, VINs) are retained for 30 days then automatically deleted.
        </div>

      </main>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowCancelModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Cancel Subscription?</h3>
              <p className="text-slate-600 mb-6">
                Are you sure you want to cancel? You'll lose access to Authorization Hook at the end of your billing period.
              </p>

              {cancelError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {cancelError}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  disabled={cancelling}
                  className="flex-1 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition disabled:opacity-50"
                >
                  Keep Subscription
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="flex-1 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center"
                >
                  {cancelling ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Canceling...
                    </>
                  ) : (
                    'Yes, Cancel'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;